import type { FilterSpecification } from 'mapbox-gl';

import { LocationType, type MapPoint } from '../types';
import {
  CITY_ZOOM,
  // COUNTY_ZOOM,
  MAX_ZOOM,
  METRO_ZOOM,
  MIN_ZOOM,
  STATE_ZOOM,
} from '../utils/constants';

export interface ChoroplethLayerConfig {
  id: LocationType;
  sourceId: string;
  fillLayerId: string;
  outlineLayerId: string;
  sourceType: 'vector' | 'geojson';
  sourceUrl?: string;
  sourceLayer?: string;
  propertyName: string;
  promoteId?: string;
  worldviewFilter?: FilterSpecification;
  isApplicable: (point: MapPoint) => boolean;
  minzoom?: number;
  maxzoom?: number;
}

export type SourceId =
  | 'regions_source'
  | 'countries_source'
  | 'metro_areas_source'
  | 'states_source'
  | 'counties_source'
  | 'cities_source';

export const ENABLED_SOURCE_IDS: readonly SourceId[] = [
  'regions_source',
  'countries_source',
  'metro_areas_source',
  'states_source',
  'counties_source',
  'cities_source',
] as const;

export const LAYER_CONFIGS: ChoroplethLayerConfig[] = [
  {
    id: LocationType.REGION,
    sourceId: 'regions_source',
    fillLayerId: 'regions_choropleth_fill',
    outlineLayerId: 'regions_choropleth_outline',
    sourceType: 'vector',
    sourceUrl: 'mapbox://reveliolabs.cmeua857x0nzn1nltfqqlyy20-5829q',
    sourceLayer: 'regionBorders',
    propertyName: 'name',
    minzoom: MIN_ZOOM,
    maxzoom: METRO_ZOOM,
    isApplicable: (point: MapPoint) =>
      point.locationType === LocationType.REGION,
  },
  {
    id: LocationType.COUNTRY,
    sourceId: 'countries_source',
    fillLayerId: 'country_choropleth_fill',
    outlineLayerId: 'country_choropleth_outline',
    sourceType: 'vector',
    sourceUrl: 'mapbox://mapbox.country-boundaries-v1',
    sourceLayer: 'country_boundaries',
    propertyName: 'iso_3166_1',
    promoteId: 'mapbox_id',
    worldviewFilter: [
      'any',
      ['==', 'all', ['get', 'worldview']],
      ['in', 'US', ['get', 'worldview']],
    ],
    minzoom: MIN_ZOOM,
    maxzoom: METRO_ZOOM,
    isApplicable: (point: MapPoint) =>
      point.locationType === LocationType.COUNTRY,
  },
  {
    id: LocationType.METRO_AREA,
    sourceId: 'metro_areas_source',
    fillLayerId: 'metro_areas_choropleth_fill',
    outlineLayerId: 'metro_areas_choropleth_outline',
    sourceType: 'vector',
    sourceUrl: 'mapbox://reveliolabs.4wn967mm',
    sourceLayer: 'metroNonMetroAreaBorders',
    propertyName: 'msa_id',
    minzoom: METRO_ZOOM,
    maxzoom: CITY_ZOOM,
    isApplicable: (point: MapPoint) =>
      point.locationType === LocationType.METRO_AREA,
  },
  {
    id: LocationType.STATE,
    sourceId: 'states_source',
    fillLayerId: 'states_choropleth_fill',
    outlineLayerId: 'states_choropleth_outline',
    sourceType: 'vector',
    sourceUrl: 'mapbox://reveliolabs.ct7yz7it',
    sourceLayer: 'adm1',
    propertyName: 'state_id',
    minzoom: STATE_ZOOM,
    maxzoom: CITY_ZOOM,
    isApplicable: (point: MapPoint) =>
      point.locationType === LocationType.STATE,
  },
  // {
  //   id: LocationType.COUNTY,
  //   sourceId: 'counties_source',
  //   fillLayerId: 'counties_choropleth_fill',
  //   outlineLayerId: 'counties_choropleth_outline',
  //   sourceType: 'vector',
  //   sourceUrl: 'mapbox://reveliolabs.arcgcugb', // TODO: replace with tileset that has county_id attribute
  //   sourceLayer: 'ADM2.zip-dx7l0ftb', // TODO: update after re-uploading tileset
  //   propertyName: 'county_id',
  //   minzoom: COUNTY_ZOOM,
  //   maxzoom: CITY_ZOOM,
  //   isApplicable: (point: MapPoint) =>
  //     point.locationType === LocationType.COUNTY,
  // },
  {
    id: LocationType.CITY,
    sourceId: 'cities_source',
    fillLayerId: 'cities_choropleth_fill',
    outlineLayerId: 'cities_choropleth_outline',
    sourceType: 'vector',
    sourceUrl: 'mapbox://reveliolabs.5cz0rfiq',
    sourceLayer: 'cityBorders_v10-3h299t',
    propertyName: 'city_id',
    minzoom: CITY_ZOOM,
    maxzoom: MAX_ZOOM,
    isApplicable: (point: MapPoint) => point.locationType === LocationType.CITY,
  },
];
